import { MapPin, Phone, Clock, ArrowRight, ExternalLink } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeIn } from "@/components/FadeIn";
import { FEATURED_CENTER, COMING_SOON_CITIES, LOGO_MARK } from "@/lib/constants";
import { useEnquiry } from "@/lib/enquiry-context";

export default function Locations() {
  const { openBook } = useEnquiry();

  return (
    <main data-testid="locations-page" className="bg-[#f7f2e8]">
      <PageHero
        crumb="Locations"
        line1="Wellness, Closer"
        line2="to Where You Live."
        subtitle="Our first center is now open in Manipal, Udupi — with new Carlton Wellness Centers coming soon to cities across India."
      />

      {/* Featured center */}
      <section data-testid="featured-center" className="max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <FadeIn>
          <p className="overline text-[#c9a14a]">Now Open</p>
        </FadeIn>
        <FadeIn delay={0.08} className="mt-8 grid lg:grid-cols-2 bg-white border border-[#c9a14a]/25 rounded-2xl overflow-hidden shadow-xl">
          <div className="relative min-h-[260px]">
            <img src={FEATURED_CENTER.image} alt={FEATURED_CENTER.name} className="absolute inset-0 w-full h-full object-cover" />
            <span className="absolute top-5 left-5 bg-[#c9a14a] text-[#050505] text-[10px] font-bold tracking-[0.22em] uppercase px-4 py-2" data-testid="featured-center-badge">
              Now Open
            </span>
          </div>
          <div className="p-8 md:p-12">
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#141414] leading-tight" data-testid="featured-center-name">
              Manipal, <span className="text-[#c9a14a]">Udupi</span>
            </h2>
            <p className="mt-2 text-[12px] font-semibold tracking-[0.14em] uppercase text-[#a8813a]">
              Carlton Wellness Centre — Karnataka
            </p>
            <div className="mt-7 space-y-5">
              <p className="flex items-start gap-4 text-[14px] text-[#4a4a4a] leading-relaxed" data-testid="featured-center-address">
                <MapPin size={17} className="text-[#c9a14a] shrink-0 mt-0.5" />
                {FEATURED_CENTER.address}
              </p>
              <a
                href={`tel:${FEATURED_CENTER.phone.replace(/\s/g, "")}`}
                data-testid="featured-center-phone"
                className="flex items-center gap-4 text-[#141414] font-semibold hover:text-[#a8813a] transition-colors"
              >
                <Phone size={17} className="text-[#c9a14a] shrink-0" />
                {FEATURED_CENTER.phone}
              </a>
              <p className="flex items-center gap-4 text-[14px] text-[#4a4a4a]">
                <Clock size={17} className="text-[#c9a14a] shrink-0" />
                {FEATURED_CENTER.hours}
              </p>
            </div>
            <div className="mt-9 flex flex-wrap gap-4">
              <button onClick={() => openBook("assessment")} data-testid="location-book-button" className="btn-gold">
                Book Your Assessment <ArrowRight size={15} strokeWidth={2} />
              </button>
              <a
                href={FEATURED_CENTER.maps}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-directions-link"
                className="btn-outline-dark"
              >
                Get Directions <ExternalLink size={14} strokeWidth={2} />
              </a>
            </div>
          </div>
        </FadeIn>

        {/* Live map */}
        <FadeIn delay={0.16} className="mt-10 rounded-2xl overflow-hidden border border-[#c9a14a]/25 shadow-xl" data-testid="featured-center-map">
          <div className="relative w-full aspect-[16/7] bg-[#0b0b0b]">
            <iframe
              title="Carlton Wellness Centre — Manipal, Udupi"
              src="https://www.google.com/maps?q=Carlton+Wellness+Centre+Sairam+Central+Mall+Manipal+Ananth+Nagar+Udupi+Karnataka+576104&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>
        </FadeIn>
      </section>

      {/* Coming soon */}
      <section data-testid="coming-soon-section" className="relative bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
          <FadeIn className="text-center">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#f7f2e8]">
              Coming Soon to <span className="text-[#c9a14a]">Your City</span>
            </h2>
            <p className="mt-4 text-sm text-[#f7f2e8]/65 max-w-md mx-auto">
              25+ Carlton Wellness Centers are planned across India. Here's where we're headed next.
            </p>
          </FadeIn>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {COMING_SOON_CITIES.map((city, i) => (
              <FadeIn
                key={city}
                delay={i * 0.06}
                data-testid={`coming-soon-${city.toLowerCase().replace(/\s+/g, "-")}`}
                className="border border-[#c9a14a]/25 p-8 text-center hover:bg-[#c9a14a]/5 transition-colors duration-500"
              >
                <img src={LOGO_MARK} alt="" className="w-9 h-9 mx-auto opacity-80" />
                <p className="mt-5 font-serif-display text-xl text-[#f7f2e8]">{city}</p>
                <p className="mt-2 text-[10px] font-bold tracking-[0.24em] uppercase text-[#c9a14a]">
                  Coming Soon
                </p>
              </FadeIn>
            ))}
          </div>
          <FadeIn className="mt-14 text-center">
            <p className="text-sm text-[#f7f2e8]/70">
              Want Carlton in your neighbourhood?{" "}
              <button
                onClick={() => openBook("franchise")}
                data-testid="locations-suggest-button"
                className="text-[#c9a14a] underline underline-offset-4 hover:text-[#e9d8a6] transition-colors"
              >
                Partner with us
              </button>
            </p>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
